import React, { useState } from 'react';
import { Download, CreditCard, Loader2 } from 'lucide-react';
import BuyMoreCredits from '../../../components/modals/BuyMoreCredits';
import UserQuotaOverview from '../../../components/dashboard/UserQuotaOverview';
import { useGetPaymentHistoryQuery } from '../../../store/services/api';
import type { PaymentHistory, PaymentHistoryResponse } from '../../../types/billing';

const BillingSettings = () => {
  const [isBuyCreditsOpen, setIsBuyCreditsOpen] = useState(false);
  const { data: response, isLoading } = useGetPaymentHistoryQuery();
  const payments = response?.data as PaymentHistory[] | undefined;

  // Tarih formatını düzenle
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-lg border border-gray-100">
        <h1 className="text-xl font-semibold mb-1">Billing Settings</h1>
        <p className="text-sm text-gray-500">Manage your credits, purchases and billing information</p>
      </div>

      {/* Credits Section */}
      <div className="bg-white p-5 rounded-lg border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-4 h-4 text-[#FF4444]" />
          <h2 className="text-base font-medium">Image Credits</h2>
        </div>

        <UserQuotaOverview 
          showBuyCredits={true}
          onBuyCreditsClick={() => setIsBuyCreditsOpen(true)}
        />
      </div>

      {/* Billing History Section */}
      <div className="bg-white p-5 rounded-lg border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Download className="w-4 h-4 text-[#FF4444]" />
          <h2 className="text-base font-medium">Billing History</h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-[#FF4444] animate-spin" />
            <span className="ml-2 text-sm text-gray-500">Loading payment history...</span>
          </div>
        ) : !payments || payments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No payment history found</p>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg divide-y divide-gray-200">
            {payments.map((item: PaymentHistory) => (
              <div key={item.id} className="p-3 flex items-center justify-between hover:bg-gray-100 transition-colors">
                <div>
                  <div className="text-base font-medium mb-0.5">${item.price.toFixed(2)}</div>
                  <div className="text-xs text-gray-600">
                    {item.photo_limit} Image Credits • {item.event_limit === 999 ? 'Unlimited' : item.event_limit} Events
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2">
                    <span>{formatDate(item.created_at)}</span>
                    {item.created_at !== item.updated_at && (
                      <span className="text-gray-400">•</span>
                    )}
                    {item.created_at !== item.updated_at && (
                      <span title="Last Updated">Updated: {formatDate(item.updated_at)}</span>
                    )}
                  </div>
                  {item.stripe_session_id && (
                    <div className="text-xs text-gray-400 mt-1 font-mono">
                      ID: {item.stripe_session_id.slice(0, 16)}...
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 ${
                    item.status === 'completed' 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : item.status === 'refunded'
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                  } rounded-full text-xs font-medium`}>
                    {item.status === 'completed' 
                      ? 'Paid' 
                      : item.status === 'refunded'
                      ? 'Refunded'
                      : 'Pending'}
                  </span>
                  {item.status === 'completed' && (
                    <button 
                      className="text-gray-400 hover:text-[#FF4444] transition-colors"
                      title="Download Invoice"
                      onClick={() => {
                        console.log('Download invoice for session:', item.stripe_session_id);
                      }}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Buy Credits Modal */}
      <BuyMoreCredits 
        isOpen={isBuyCreditsOpen}
        onClose={() => setIsBuyCreditsOpen(false)}
      />
    </div>
  );
};

export default BillingSettings;